import { useCallback, useMemo, useState } from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import store, { useSelector, type RootState } from 'store';
import { DEV_REPLACE_STATE_TYPE } from 'store';

const SLICE_KEYS = [
  'ui',
  'iam',
  'chat',
  'careCircle',
  'navigation',
  'cms',
  'registration',
  'clientReferral',
  'onboardingV3',
] as const satisfies readonly (keyof RootState)[];

type SliceKey = (typeof SLICE_KEYS)[number];

const ALL = '__all__' as const;
type Selection = typeof ALL | SliceKey;

export const DevStateInspectorTab = () => {
  const fullState = useSelector((state: RootState) => state);
  const [selected, setSelected] = useState<Selection>(ALL);
  const [jsonValue, setJsonValue] = useState('');
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const currentJson = useMemo(() => {
    const data = selected === ALL ? fullState : fullState[selected as SliceKey];
    return JSON.stringify(data, null, 2);
  }, [fullState, selected]);

  const displayJson = isDirty ? jsonValue : currentJson;

  const handleSelectionChange = useCallback((value: Selection) => {
    setSelected(value);
    setIsDirty(false);
    setError('');
  }, []);

  const handleJsonChange = useCallback((value: string) => {
    setJsonValue(value);
    setIsDirty(true);
    setError('');
  }, []);

  const handleApply = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      const newState =
        selected === ALL ? parsed : { ...store.getState(), [selected]: parsed };
      store.dispatch({ type: DEV_REPLACE_STATE_TYPE, payload: newState });
      setIsDirty(false);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }, [jsonValue, selected]);

  const handleReset = useCallback(() => {
    setIsDirty(false);
    setError('');
  }, []);

  return (
    <Stack sx={{ gap: 2, pt: 3 }}>
      <TextField
        select
        label='Slice'
        value={selected}
        onChange={(e) => handleSelectionChange(e.target.value as Selection)}
        size='small'
      >
        <MenuItem value={ALL}>All slices</MenuItem>
        {SLICE_KEYS.map((key) => (
          <MenuItem key={key} value={key}>
            {key}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        multiline
        value={displayJson}
        onChange={(e) => handleJsonChange(e.target.value)}
        slotProps={{
          input: {
            sx: {
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.4,
            },
          },
        }}
        minRows={20}
        maxRows={40}
      />

      {error && (
        <Typography color='error' variant='body2'>
          {error}
        </Typography>
      )}

      <Stack direction='row' sx={{ gap: 1 }}>
        <Button variant='contained' onClick={handleApply} disabled={!isDirty}>
          Apply
        </Button>
        <Button variant='outlined' onClick={handleReset} disabled={!isDirty}>
          Reset
        </Button>
      </Stack>
    </Stack>
  );
};
